/* eslint-disable */
import axios from "axios";
import React, { useEffect, useState } from "react";

enum ResultType {
    BUSINESS_DESCRIPTION_VALIDATION = "BUSINESS_DESCRIPTION_VALIDATION",
    BUSINESS_NAME_VALIDATION = "BUSINESS_NAME_VALIDATION",
    BUSINESS_INFO_VALIDATION = "BUSINESS_INFO_VALIDATION"
}

interface GoogleBusinessValidationRecord {
    id?: number;
    landing_page?: number;
    did_pass_validation: boolean;
    result_type: ResultType | string;
    result: {
        failure_reasons?: string[];
        [key: string]: any;
    };
    created?: string;
}

interface GBPValidationHistoryProps {
    slug: string;
}

const GBPValidationHistory: React.FC<GBPValidationHistoryProps> = ({ slug }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [validations, setValidations] = useState<GoogleBusinessValidationRecord[]>(
        []
    );
    const [expandedIndexes, setExpandedIndexes] = useState<Record<number, boolean>>(
        {}
    );
    const [showHistory, setShowHistory] = useState<boolean>(false);

    useEffect(() => {
        async function fetchValidations() {
            if (!slug) return;
            setIsLoading(true);
            setErrorMessage("");
            try {
                const url = `${process.env.REACT_APP_SERVER_URL}/api/landing-pages/${slug}/gmb/validations`;
                const response = await axios.get(url);
                const data: GoogleBusinessValidationRecord[] = response?.data || [];
                setValidations(Array.isArray(data) ? data : []);
            } catch (error: any) {
                setErrorMessage(
                    error?.response?.data?.message || "Failed to load validations"
                );
            }
            setIsLoading(false);
        }

        fetchValidations();
    }, [slug]);

    function toggleExpanded(index: number) {
        setExpandedIndexes((prev) => ({ ...prev, [index]: !prev[index] }));
    }

    function formatDate(dateString?: string): string {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString(); // Formats the date to a readable string
    }

    return (
        <div className="bg-white m-12 rounded-lg p-6">
            <div className="pb-4">
                <h2>Google Business Profile Validation History</h2>
            </div>
            {isLoading && (
                <div className="flex items-center">
                    <img src="/loading.gif" width="20px" height="20px" alt="loading" />
                    <span className="ml-2 text-sm text-gray-600">Loading...</span>
                </div>
            )}
            {errorMessage && (
                <div className="text-red-600 text-sm">{errorMessage}</div>
            )}
            {!isLoading && !errorMessage && !validations.length && (
                <div className="text-sm text-gray-600">No validation records found.</div>
            )}
            {!isLoading && !errorMessage && validations.length > 0 && (
                <div className="space-y-4">
                    {validations.map((v, idx) => {
                        if (!showHistory && idx > 0) return null;
                        return (
                            <div key={v.id ?? idx} className="border rounded-md p-4 text-left">
                                <div className="flex justify-between items-center">
                                    <div className="text-sm text-gray-500">
                                        {formatDate(v.created)}
                                    </div>
                                    <div className="text-sm text-gray-700 font-medium">
                                        {v.result_type?.split("_")?.join(" ")}
                                    </div>
                                    <div className={v.did_pass_validation ? "text-green-700" : "text-red-700"}>
                                        {v.did_pass_validation ? "Passed" : "Failed"}
                                    </div>
                                </div>
                                {/* Show failure reasons if they exist */}
                                {v.result?.failure_reasons && v.result.failure_reasons.length > 0 && (
                                    <div className="mt-3">
                                        <div className="text-sm font-medium">Failure reasons</div>
                                        <ul className="list-disc ml-5 text-sm">
                                            {v.result?.failure_reasons?.map((r: string, i: number) => (
                                                <li key={i}>{r}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Show details if expanded */}
                                <button
                                    className="mt-3 text-xs text-blue-600 underline"
                                    onClick={() => toggleExpanded(idx)}
                                >
                                    {expandedIndexes[idx] ? "Hide details" : "Show details"}
                                </button>
                                {expandedIndexes[idx] && (
                                    <pre className="mt-3 bg-gray-50 rounded p-3 overflow-auto text-xs">
                                        {JSON.stringify(v.result, null, 2)}
                                    </pre>
                                )}
                            </div>
                        );
                    })}
                    {validations.length > 1 && (
                        <div className="pt-2">
                            <button
                                className="text-xs text-blue-600 underline"
                                onClick={() => setShowHistory((prev) => !prev)}
                            >
                                {showHistory ? "Hide" : "Show More"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GBPValidationHistory;